/**
 * `/metal-resources/references/faq/`: the searchable Frequently Asked Questions page, built for
 * both brands (there is no `NEXT_PUBLIC_COMPANY` check here).
 *
 * At build time `getStaticProps` fetches every FAQ entry in all three languages with
 * `src/contentful/faq/getContentfulFAQ.ts` (already filtered to this brand; a Contentful failure
 * fails the build). The page renders `src/components/FAQ/FAQ.tsx`, which groups, filters and
 * searches the entries in the browser.
 *
 * Copy comes from two places: the hero and SEO fields from
 * `src/content/shared/metal-resources/references/faq/index.ts`, and the contact panel and search
 * labels from the glossary page's `GlossaryPageContent`
 * (`src/pages/metal-resources/references/glossary/index.tsx`), which the two pages share. That
 * export is not the `GlossaryPageContent` of the glossary content module, which is the site-search
 * entry.
 *
 * Smoke-tested, with `FAQ` stubbed, by `src/unit-test/MetalResources/FAQ.spec.tsx`.
 */
import React from "react";
import { useApplication } from "@ryerson/frontend.application";
import FAQ from "@components/FAQ/FAQ";
import { GlossaryPageContent } from "../glossary";
import MetaHead from "@components/Shared/model/MetaHead";
import { useHasMounted } from "@components/Hooks";
import { allContentfulHeaderCalls, HeaderPageProps } from "@ryerson/frontend.contentful";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import getContentfulFAQ from "@contentful/faq/getContentfulFAQ";
import { ProcessedContentfulFAQsByLocale } from "@contentful/faq/types";
import { FaqHeroContent, FaqMetaObject } from "@content/shared/metal-resources/references/faq";

/** Build-time props: the header and footer data plus every FAQ, keyed by language. */
export const getStaticProps = async () => {
	const { props: headerFooterProps } = await allContentfulHeaderCalls();
	return {
		props: {
			...headerFooterProps,
			faqsByLocale: await getContentfulFAQ(),
		},
	};
};

/** Renders the FAQ page in the visitor's language. */
const Page: React.FC<HeaderPageProps & { faqsByLocale: ProcessedContentfulFAQsByLocale }> = (
	props
) => {
	const {
		localization: { language },
	} = useApplication();

	// Renders nothing until mounted in the browser, so the exported HTML has no FAQ content.
	// `components/NextLayouts/RootLayout.tsx` already holds every page back the same way, with
	// `HasMounted`.
	if (!useHasMounted()) return null;

	return (
		<HeaderLayout {...getHeaderData(props)}>
			<MetaHead content={FaqMetaObject[language]} />
			<FAQ
				content={GlossaryPageContent[language]}
				hero={FaqHeroContent[language]}
				faqsByLocale={props.faqsByLocale}
			/>
		</HeaderLayout>
	);
};

export default Page;
